// ScheduledPage — articles queued for auto-publish.
//
// Sorted by `publish_at` ascending so the next-to-publish floats to the
// top. Each item shows a clear "publishes in N hours" hint.

import { useEffect, useState } from "react";
import { list, fileUrl } from "../lib/pb";
import { navigate } from "../App";
import { useT } from "../lib/i18n";
import { kindLabel } from "./ArticlesPage";
import type { Article } from "../lib/types";

export default function ScheduledPage() {
  const t = useT();
  const [items, setItems] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    list<Article>("articles", {
      perPage: 100,
      sort: "publish_at",
      filter: "status='scheduled'",
      expand: "author",
    })
      .then((r) => setItems(r.items))
      .catch((e) => alert(`Failed to load scheduled: ${(e as Error).message}`))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">{t.scheduledTitle}</h1>
        <p className="page-subtitle">{t.scheduledSubtitle(items.length)}</p>
      </div>

      {loading ? (
        <div className="spinner-line">{t.loading}</div>
      ) : items.length === 0 ? (
        <div className="empty">{t.scheduledEmpty}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {items.map((a) => {
            const at = a.publish_at ? new Date(a.publish_at) : null;
            const inMs = at ? at.getTime() - Date.now() : null;
            return (
              <div
                key={a.id}
                className="card card-hover"
                style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", cursor: "pointer" }}
                onClick={() => navigate(`/article/${a.id}`)}
              >
                {a.cover ? (
                  <img
                    src={fileUrl("articles", a.id, a.cover, "120x120")}
                    alt=""
                    style={{ width: 56, height: 56, objectFit: "cover", borderRadius: "var(--radius-md)", flexShrink: 0 }}
                  />
                ) : (
                  <div style={{ width: 56, height: 56, background: "var(--bg-3)", borderRadius: "var(--radius-md)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-4)" }}>
                    ◷
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "var(--font-serif)", fontSize: 15, fontWeight: 600, color: "var(--text-0)" }}>
                    {a.title || "(untitled)"}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4 }}>
                    {a.expand?.author?.name || "—"} · {kindLabel(t, a.kind)}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 13, color: "var(--accent)", fontWeight: 500 }}>
                    {at ? at.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—"}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                    {inMs == null ? "" : inMs < 0 ? t.scheduledOverdue : publishesInLabel(t, inMs)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function publishesInLabel(t: ReturnType<typeof useT>, ms: number): string {
  const hr = Math.max(1, Math.round(ms / 3600000));
  return t.scheduledPublishesIn(hr);
}
